import { cva, type VariantProps } from "class-variance-authority";
import { clsx } from "clsx";
import { minidenticon } from "minidenticons";
import { type ComponentProps, useMemo } from "react";

import LoadableImage from "common/components/ui/LoadableImage";
import useWorkspaceAccounts from "common/datahooks/useWorkspaceAccounts";

const saturation = 75;
const lightness = 50;

const workspaceImageVariants = cva("", {
  variants: {
    size: {
      sm: "size-7 rounded-xl",
      md: "size-10 rounded-2xl",
      lg: "size-14 rounded-2xl",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export type WorkspaceImageProps = Omit<
  ComponentProps<typeof LoadableImage>,
  "src"
> &
  VariantProps<typeof workspaceImageVariants> & {
    id: string;
    imageSrc: string;
  };

export default function WorkspaceImage({
  id,
  imageSrc,
  size,
  className,
  ...props
}: WorkspaceImageProps) {
  const { workspaceAccounts } = useWorkspaceAccounts(id);

  const svgURI = useMemo(
    () =>
      `data:image/svg+xml;utf8,${encodeURIComponent(
        minidenticon(id, saturation, lightness),
      )}`,
    [id],
  );

  return (
    <div className={clsx("relative shrink-0", className)}>
      <LoadableImage
        {...props}
        className={workspaceImageVariants({
          size,
          className: !imageSrc && "border border-black-200",
        })}
        src={imageSrc || svgURI}
      />
    </div>
  );
}
